import { Popover } from "@base-ui/react/popover"
import { ChevronDownIcon } from "@heroicons/react/16/solid"
import { cn } from "@src/utils/cn"
import clsx from "clsx"
import Link from "next/link"
import {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  useState,
} from "react"
import { ActionPopover } from "./ActionPopover"

type PopoverItem = {
  label: string
  href?: string
  onClick?: () => void
  icon?: ComponentType<{ className?: string }>
}

type ListItemProps = {
  children: ReactNode
  popoverItems?: PopoverItem[]
  onClick?: () => void
  href?: string
  highlight?: boolean
  className?: string
  dataTestId?: string
  disabled?: boolean
  ariaLabel?: string
}

function ListItem({
  children,
  popoverItems,
  onClick,
  href,
  highlight = false,
  className,
  dataTestId,
  disabled = false,
  ariaLabel = "Select item",
}: ListItemProps) {
  const [isOpen, setIsOpen] = useState(false)
  const hasPopover = popoverItems && popoverItems.length > 0
  const isInteractive =
    !disabled && (hasPopover || onClick !== undefined || href !== undefined)

  const renderContent = (triggerProps?: ComponentProps<"button">) => (
    <div
      className={clsx(
        "group/item -mx-3 sm:-mx-4 relative rounded-2xl px-3 sm:px-4",
        highlight && "bg-gray-100 dark:bg-gray-800",
        disabled && "opacity-50",
        isInteractive &&
          "group focus-within:bg-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 dark:focus-within:bg-gray-800",
        isOpen && "bg-gray-100 dark:bg-gray-800",
        className
      )}
      data-testid={dataTestId}
      data-highlight={highlight || isOpen || undefined}
    >
      <div className="flex items-center gap-3 py-4">
        <div className="flex min-w-0 flex-1 items-center gap-3">{children}</div>
        {hasPopover && (
          <ChevronDownIcon
            className={clsx(
              "size-5 shrink-0 text-gray-500 transition-transform duration-100 ease-in-out group-hover:text-gray-900 dark:group-hover:text-white",
              isOpen && "rotate-180"
            )}
          />
        )}
      </div>

      {isInteractive &&
        !hasPopover &&
        (href ? (
          href.startsWith("/") ? (
            <Link
              href={href}
              onClick={onClick}
              className="absolute inset-0 z-10 rounded-2xl focus-visible:outline-none"
              aria-label={ariaLabel}
            />
          ) : (
            <a
              href={href}
              onClick={onClick}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 z-10 rounded-2xl focus-visible:outline-none"
            >
              <span className="sr-only">{ariaLabel}</span>
            </a>
          )
        ) : (
          <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={clsx(
              "absolute inset-0 z-10 rounded-2xl focus-visible:outline-none",
              disabled && "cursor-not-allowed"
            )}
            aria-label={ariaLabel}
          />
        ))}
      {hasPopover && (
        <Popover.Trigger
          {...triggerProps}
          type="button"
          disabled={disabled}
          className="absolute inset-0 z-10 rounded-2xl focus-visible:outline-none"
          aria-label="Open menu"
        />
      )}
    </div>
  )

  const content = renderContent()

  if (!hasPopover) return content

  return (
    <ActionPopover
      open={isOpen}
      onOpenChange={setIsOpen}
      showArrow
      align="center"
      sideOffset={0}
      trigger={(props) => renderContent(props)}
      items={popoverItems}
    />
  )
}

const Content = ({
  align = "start",
  className,
  children,
}: {
  align?: "start" | "end"
  className?: string
  children: ReactNode
}) => (
  <div
    className={cn(
      "flex flex-col gap-0.5",
      align === "start" ? "min-w-0 flex-1 items-start" : "items-end text-right",
      className
    )}
  >
    {children}
  </div>
)

const Title = ({
  children,
  className,
  hideValue = false,
}: {
  children: ReactNode
  className?: string
  hideValue?: boolean
}) => (
  <div
    className={cn(
      "max-w-full",
      hideValue
        ? "-tracking-[0.07em] font-extrabold text-gray-700 text-xl/5 dark:text-gray-300"
        : "font-semibold text-base/5 text-gray-900 dark:text-white",
      className
    )}
  >
    {hideValue ? "••••" : children}
  </div>
)

const Subtitle = ({
  children,
  className,
  hideValue = false,
}: {
  children: ReactNode
  className?: string
  hideValue?: boolean
}) => (
  <div
    className={cn(
      hideValue
        ? "-tracking-[0.07em] font-extrabold text-base/4 text-gray-400 dark:text-gray-600"
        : "font-medium text-gray-500 text-sm/4 dark:text-gray-400",
      className
    )}
  >
    {hideValue ? "••••" : children}
  </div>
)

const Icon = ({
  children,
  badgeIcon,
}: {
  children: ReactNode
  badgeIcon?: ReactNode
}) => (
  <div className="relative size-10 shrink-0">
    {badgeIcon && (
      <div className="-top-1.5 -left-1.5 absolute z-2 size-5.5 rounded-full ring-2 ring-gray-25 *:size-full group-focus-within:ring-gray-100 group-hover:ring-gray-100 group-data-highlight/item:ring-gray-100 dark:ring-gray-850 dark:group-data-highlight/item:ring-gray-800 dark:group-hover:ring-gray-800 dark:group-focus-within:ring-gray-800">
        {badgeIcon}
      </div>
    )}
    <div className="relative size-full overflow-hidden rounded-full">
      {badgeIcon && (
        <div className="-top-2.5 -left-2.5 absolute z-1 size-7.5 rounded-full bg-gray-900/20" />
      )}
      {children}
    </div>
  </div>
)

ListItem.Content = Content
ListItem.Title = Title
ListItem.Subtitle = Subtitle
ListItem.Icon = Icon

export default ListItem
